import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Typography,
  Stack,
  IconButton,
} from '@mui/material';
import { ReactComponent as PrintIcon } from '../../../../../assets/SVG/printerblue.svg';
import CustomPagination from '../../../../../components/common/CustomPagination';
import TableSkeleton from '../../../../../components/common/TableSkeleton';
import useCurrency from '../../../../../components/CurrencyProvider';
import style from '../../../Advance/Advance.module.css';
import { getRequest } from '../../../../../services/httpService';
import { useAppQuery } from '../../../../../services/appQuery';
import { getRefundListsURL, RefundHistoryListResponse, RefundHistoryItem } from '../../Refund.helper';
import ReusableReportComponent from '../../../../../components/printReports/ReusableReportComponent';
import AdvanceRefundReceiptComponent from '../../AdvanceRefundReceiptComponent';
import { useParams } from 'react-router-dom';
import { convertDateToDDMMYYYY } from '../../../../../utils/commonUtils';
import { useTranslation } from 'react-i18next';


const AdvanceRefundLogs: React.FC = () => {
  const { formatCurrency } = useCurrency();
  const { t: tBilling } = useTranslation('billing_config');
  const [page, setPage] = useState(0);
  const { id } = useParams<{ id?: string; refund_id?: string }>();
  const patientId = Number(id);
  const [pageSize, setPageSize] = useState(10);
  const [showPrint, setShowPrint] = useState<boolean>(false);
  const [receiptData, setReceiptData] = useState<RefundHistoryItem | null>(null);
  
  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setPage(0);
  }, []);


  const { data: RefundLogs, isLoading } = useAppQuery<RefundHistoryListResponse>(
    ['RefundLogs', patientId],
    () => getRequest<RefundHistoryListResponse>(`${getRefundListsURL()}history/?refund_type=ADVANCE&patient_id=${patientId}`),
    {
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: true,
    }
  );

  const columns = useMemo(
    () => [
      tBilling('RefundDetails.logsColumns.refundDate', 'Refund Date'),
      tBilling('Refund.receipt_no', 'Bill/Advance Number'),
      tBilling('Refund.refund_receipt_no', 'Receipt No.'),
      tBilling('RefundDetails.logsColumns.refundAmount', 'Refund Amount'),
      tBilling('RefundDetails.logsColumns.refundStatus', 'Refund Status'),
      tBilling('RefundDetails.logsColumns.refundRejectBy', 'Refund/Reject By'),
      tBilling('Refund.refund_reason', 'Reason'),
      tBilling('Advance.action', 'Action'),

    ],
    [tBilling]
  );


  const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handlePrintReceipt = useCallback((row: RefundHistoryItem) => {
    setReceiptData(row);
    setShowPrint(true);
  }, []);

  const handleClose = useCallback(() => {
    setShowPrint(false);
  }, []);


  return (
    <Box sx={{ mt: 2 }} data-testid="advance-refund-logs-container">
      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 'none', elevation: 0 }} data-testid="advance-refund-logs-table-container">
        <Table aria-label={tBilling('RefundDetails.aria.advanceRefundLogsTable', 'Advance refund logs list')} data-testid="advance-refund-logs-table">
          <TableHead>
            <TableRow sx={{ bgcolor: '#FAFAFA' }}>
              {columns.map((col) => (
                <TableCell
                  key={col}
                  className={style.tableHeader}
                  sx={{ whiteSpace: 'nowrap' }}
                  align={col === tBilling('Advance.action', 'Action') ? 'right' : 'left'}
                >
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableSkeleton
                columnWidths={[120, 160, 260, 160, 180, 200, 260, 120]}
                rows={pageSize}
              />
            )}

            {!isLoading &&
							RefundLogs?.objects.map((row) => (
							  <TableRow hover key={row.history_id}>
							    <TableCell className={style.tableCells}>{convertDateToDDMMYYYY(row.history_date)}</TableCell>
							    <TableCell className={style.tableCells}>{row.bill_advance_number ?? '-'}</TableCell>
							    <TableCell className={style.tableCells}>{row.receipt_number ?? '-'}</TableCell>
							    <TableCell className={style.tableCells} >
							      {formatCurrency(Number(row.refund_amount ?? 0 ))}
							    </TableCell>
							    <TableCell className={style.tableCells} >
							      {row.status ?? '-'}
							    </TableCell>

							    <TableCell className={style.tableCells}>{row.history_user?.full_name   ?? '-'}</TableCell>
							    <TableCell className={style.tableCells}>{row.refund_reason ?? '-'}</TableCell>
							    <TableCell className={style.tableCells}  sx={{ minWidth: 120 }}>
							      <Stack
							        direction="row"
							        spacing={1}
							        justifyContent="flex-end"
							        alignItems="center"
							      >
							        <IconButton size="small" aria-label={tBilling('RefundDetails.aria.printAdvanceRefundLog', 'Print advance refund log')} onClick={() => handlePrintReceipt(row)}>
							          <PrintIcon />
							        </IconButton>

							      </Stack>
							    </TableCell>
							  </TableRow>
							))}

            {!isLoading && RefundLogs?.meta.total_count === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                    <Typography variant="body2">{tBilling('Refund.no_entries_found', 'No entries found.')}</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomPagination
        page={page}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        pageSizeOptions={[10, 15, 25, 50]}
        totalCount={RefundLogs?.meta.total_count ?? 0}
        onPageChange={handlePageChange}
      />

      <ReusableReportComponent
        open={showPrint}
        onClose={handleClose}
        reportTitle={tBilling('Refund.advance_refund_receipt', 'Advance Refund Receipt')}
      >
        {receiptData && <AdvanceRefundReceiptComponent data={receiptData} />}
      </ReusableReportComponent>
    </Box>
  );
};

export default AdvanceRefundLogs;

